import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FolderKanban, Languages, Settings, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import {
  useWorkspace,
  useWorkspaceLanguages,
  useWorkspaceMembers,
  useWorkspaceProjects,
  useWorkspaceRole,
} from '@/lib/api';

type WorkspaceTab = 'projects' | 'members' | 'languages' | 'settings';

const ICONS: Record<WorkspaceTab, typeof FolderKanban> = {
  projects: FolderKanban,
  members: Users,
  languages: Languages,
  settings: Settings,
};

export function WorkspaceDetailPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const ws = useWorkspace(wsId);
  // Counts for the tab badges — cheap because they share TanStack Query cache
  // with the per-tab views below.
  const members = useWorkspaceMembers(wsId);
  const languages = useWorkspaceLanguages(wsId);
  const projects = useWorkspaceProjects(wsId);
  const { canManage } = useWorkspaceRole(wsId);

  const tab: WorkspaceTab = location.pathname.endsWith('/members')
    ? 'members'
    : location.pathname.endsWith('/languages')
      ? 'languages'
      : location.pathname.endsWith('/settings')
        ? 'settings'
        : 'projects';

  const onTabChange = (next: WorkspaceTab) => {
    if (next === 'projects') void navigate(`/workspaces/${wsId}`);
    else void navigate(`/workspaces/${wsId}/${next}`);
  };

  return (
    <div className="space-y-6">
      <Link
        to="/workspaces"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Workspaces
      </Link>
      <header>
        {ws.isLoading ? (
          <Spinner label="Loading workspace…" />
        ) : ws.isError ? (
          <p
            role="alert"
            className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
          >
            Failed to load workspace: {ws.error.message}
          </p>
        ) : (
          <>
            <h1 className="text-2xl font-semibold tracking-tight">{ws.data?.name}</h1>
            <p className="mt-1 inline-flex items-center gap-2 text-xs text-muted-foreground">
              <span>{ws.data?.slug}</span>
              <span aria-hidden>·</span>
              <span>plan: {ws.data?.plan ?? 'free'}</span>
            </p>
          </>
        )}
      </header>

      <Tabs<WorkspaceTab>
        value={tab}
        onChange={onTabChange}
        ariaLabel="Workspace sections"
        items={[
          {
            value: 'projects',
            label: tabLabel('projects', 'Projects'),
            badge: <Badge tone="outline">{projects.data?.length ?? 0}</Badge>,
          },
          {
            value: 'members',
            label: tabLabel('members', 'Members'),
            badge: <Badge tone="outline">{members.data?.length ?? 0}</Badge>,
          },
          {
            value: 'languages',
            label: tabLabel('languages', 'Languages'),
            badge: <Badge tone="outline">{languages.data?.length ?? 0}</Badge>,
          },
          // Settings (credentials + AI config) is owner/admin-only. The route is
          // also guarded inside the tab, so a deep link still renders a forbidden
          // state for viewers/translators rather than crashing.
          ...(canManage
            ? [{ value: 'settings', label: tabLabel('settings', 'Settings') } satisfies TabItem<WorkspaceTab>]
            : []),
        ]}
      />
      <Outlet />
    </div>
  );
}

function tabLabel(tab: WorkspaceTab, label: string) {
  const Icon = ICONS[tab];
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="size-3.5" aria-hidden />
      {label}
    </span>
  );
}
